import type { Shape } from '@hcie/shared';

type HandleId = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

const HANDLE_IDS: HandleId[] = ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'];

export type OverlayCallbacks = {
    onCommit: () => void;
    onCancel: () => void;
    onHandleStart: (handle: HandleId, e: MouseEvent) => void;
    onMoveStart: (e: MouseEvent) => void;
    onRotateStart: (e: MouseEvent) => void;
};

export type VectorOverlay = {
    root: HTMLDivElement;
    controls: HTMLDivElement;
    handles: Record<HandleId, HTMLDivElement>;
    rotateHandle: HTMLDivElement;
    destroy: () => void;
    setVisible: (v: boolean) => void;
    update: (shape: Shape, zoom: number) => void;
};

function cssPx(n: number) {
    return `${Math.round(n)}px`;
}

function clamp(n: number, min: number, max: number) {
    if (n < min) return min;
    if (n > max) return max;
    return n;
}

function handleCursor(id: HandleId) {
    if (id === 'n' || id === 's') return 'ns-resize';
    if (id === 'e' || id === 'w') return 'ew-resize';
    if (id === 'nw' || id === 'se') return 'nwse-resize';
    return 'nesw-resize';
}

function rotatePoint(p: { x: number; y: number }, center: { x: number; y: number }, angleDegrees: number) {
    if (angleDegrees === 0) return p;
    const rad = angleDegrees * Math.PI / 180;
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    return {
        x: center.x + dx * Math.cos(rad) - dy * Math.sin(rad),
        y: center.y + dx * Math.sin(rad) + dy * Math.cos(rad)
    };
}

export function createVectorOverlay(callbacks: OverlayCallbacks): VectorOverlay {
    const root = document.createElement('div');
    root.id = 'vector-overlay-root'; // For easier debugging
    root.style.cssText = [
        'position:absolute',
        'left:0',
        'top:0',
        'width:0',
        'height:0',
        // Must be interactive (OK/Cancel + handles).
        'pointer-events:auto',
        'z-index:1005',
    ].join(';');

    const controls = document.createElement('div');
    controls.style.cssText = [
        'position:absolute',
        'display:flex',
        'gap:6px',
        'align-items:center',
        'pointer-events:auto',
        'z-index:1006',
    ].join(';');

    const move = document.createElement('button');
    move.innerText = 'Move';
    move.style.cssText = 'background:#333;color:#fff;border:none;padding:2px 8px;border-radius:2px;cursor:move;font-size:11px;';
    move.onmousedown = (e) => callbacks.onMoveStart(e);

    const ok = document.createElement('button');
    ok.innerText = 'OK';
    ok.style.cssText = 'background:#28a745;color:#fff;border:none;padding:2px 8px;border-radius:2px;cursor:pointer;font-size:11px;font-weight:bold;';
    ok.onclick = (e) => { e.stopPropagation(); callbacks.onCommit(); };

    const cancel = document.createElement('button');
    cancel.innerText = 'Cancel';
    cancel.style.cssText = 'background:#dc3545;color:#fff;border:none;padding:2px 8px;border-radius:2px;cursor:pointer;font-size:11px;font-weight:bold;';
    cancel.onclick = (e) => { e.stopPropagation(); callbacks.onCancel(); };

    controls.appendChild(move);
    controls.appendChild(ok);
    controls.appendChild(cancel);
    root.appendChild(controls);

    const handles = {} as Record<HandleId, HTMLDivElement>;
    for (const id of HANDLE_IDS) {
        const h = document.createElement('div');
        h.style.cssText = [
            'position:absolute',
            'width:10px',
            'height:10px',
            'border:2px solid #00bcd4',
            'background:#fff',
            'border-radius:2px',
            'box-sizing:border-box',
            'pointer-events:auto',
            `cursor:${handleCursor(id)}`,
        ].join(';');
        h.onmousedown = (e) => callbacks.onHandleStart(id, e);
        handles[id] = h;
        root.appendChild(h);
    }

    // Single rotation handle (more explicit icon).
    const rotateHandle = document.createElement('div');
    rotateHandle.style.cssText = [
        'position:absolute',
        'width:22px',
        'height:22px',
        'border:2px solid #00bcd4',
        'background:rgba(0,188,212,0.15)',
        'border-radius:50%',
        'box-sizing:border-box',
        'pointer-events:auto',
        'cursor:grab',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'color:#00bcd4',
        'font-weight:800',
        'font-size:14px',
        'user-select:none',
    ].join(';');
    rotateHandle.innerHTML = '↻';
    rotateHandle.onmousedown = (e) => callbacks.onRotateStart(e);
    root.appendChild(rotateHandle);

    const destroy = () => {
        root.remove();
    };

    const setVisible = (v: boolean) => {
        root.style.display = v ? 'block' : 'none';
    };

    const update = (shape: Shape, zoom: number) => {
        // All coordinates are document pixels; wrapper is CSS-scaled to zoom already.
        let tx1 = Math.min(shape.x1, shape.x2);
        let ty1 = Math.min(shape.y1, shape.y2);
        let tx2 = Math.max(shape.x1, shape.x2);
        let ty2 = Math.max(shape.y1, shape.y2);

        // Circle uses center (x1,y1) and radius point (x2,y2) in renderer.
        const isCircle = (shape as any).type === 'circle';
        if (isCircle) {
            const cx = shape.x1;
            const cy = shape.y1;
            const r = Math.hypot(shape.x2 - shape.x1, shape.y2 - shape.y1);
            tx1 = cx - r;
            ty1 = cy - r;
            tx2 = cx + r;
            ty2 = cy + r;
        }

        // Root at top-left so handle positions are absolute in wrapper coords.
        root.style.left = '0px';
        root.style.top = '0px';

        // Place controls near top-left of bbox (don't rotate controls themselves, but keep them near rotated shape?)
        // For simplicity, we keep controls at the unrotated top-left of the bounding box.
        controls.style.left = cssPx(tx1 * zoom);
        controls.style.top = cssPx((ty1 * zoom) - 26);

        // Keep controls visible inside wrapper (large shapes can push it out).
        const wrapper = root.parentElement as HTMLElement | null;
        if (wrapper) {
            const maxLeft = Math.max(0, wrapper.clientWidth - controls.offsetWidth);
            const maxTop = Math.max(0, wrapper.clientHeight - controls.offsetHeight);
            const left = clamp(tx1 * zoom, 0, maxLeft);
            const top = clamp((ty1 * zoom) - 26, 0, maxTop);
            controls.style.left = cssPx(left);
            controls.style.top = cssPx(top);
        }

        const midX = (tx1 + tx2) / 2;
        const midY = (ty1 + ty2) / 2;
        const angle = (shape as any).style?.angle || 0;
        const isLine = (shape as any).type === 'line';
        const needsRotation = !isLine && angle !== 0;

        let pos: Record<HandleId, { x: number; y: number }>;
        if (isCircle) {
            const cx = shape.x1;
            const cy = shape.y1;
            const r = Math.hypot(shape.x2 - shape.x1, shape.y2 - shape.y1);
            const d = r / Math.SQRT2;
            pos = {
                n: { x: cx, y: cy - r },
                s: { x: cx, y: cy + r },
                e: { x: cx + r, y: cy },
                w: { x: cx - r, y: cy },
                ne: { x: cx + d, y: cy - d },
                nw: { x: cx - d, y: cy - d },
                se: { x: cx + d, y: cy + d },
                sw: { x: cx - d, y: cy + d },
            };
        } else {
            pos = {
                nw: { x: tx1, y: ty1 },
                ne: { x: tx2, y: ty1 },
                sw: { x: tx1, y: ty2 },
                se: { x: tx2, y: ty2 },
                n: { x: midX, y: ty1 },
                s: { x: midX, y: ty2 },
                w: { x: tx1, y: midY },
                e: { x: tx2, y: midY },
            };
        }

        for (const id of HANDLE_IDS) {
            let p = pos[id];
            let px = p.x * zoom;
            let py = p.y * zoom;

            if (needsRotation) {
                const rot = rotatePoint({ x: px, y: py }, { x: midX * zoom, y: midY * zoom }, angle);
                px = rot.x;
                py = rot.y;
            }

            handles[id].style.left = cssPx(px - 5);
            handles[id].style.top = cssPx(py - 5);
        }

        // Place rotate handle above top-center of bbox.
        let rpx = midX * zoom;
        let rpy = (ty1 * zoom) - 44;
        
        if (needsRotation) {
            const rot = rotatePoint({ x: rpx, y: rpy }, { x: midX * zoom, y: midY * zoom }, angle);
            rpx = rot.x;
            rpy = rot.y;
        }
        
        rotateHandle.style.left = cssPx(rpx - 11);
        rotateHandle.style.top = cssPx(rpy);
    };

    return { root, controls, handles, rotateHandle, destroy, setVisible, update };
}

